import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "./common/Button";
import { useMediaQuery } from "react-responsive";
import SocialMediaRow from "./common/SocialMediaRow";
import { getScrollRefByKey, scrollToRef } from "../backend/scrollToRef";
import { getMainInfoByName } from "../backend/info/mainInfo";
import { getWeekends, loadWeekends, Weekend } from "../backend/weekends/weekend";

const Menu = ({ onClose }) => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const isMedium = useMediaQuery({ query: "(max-width: 600px)" });
  const isSmall = useMediaQuery({ query: "(max-width: 450px)" });
  const isDesktopSmall = useMediaQuery({ query: "(max-width: 1000px)" });

  const navigate = useNavigate();
  const location = useLocation();

  const [isHoveringTest, setIsHoveringTest] = useState(false);
  const [isHoveringSupport, setIsHoveringSupport] = useState(false);

  const [isOpenTest, setIsOpenTest] = useState(false);
  const [isOpenSupport, setIsOpenSupport] = useState(false);

  const [weekends, setWeekends] = useState<Weekend[]>([]);

  // Load weekends on mount — use cache if already populated, else fetch
  useEffect(() => {
    const cached = getWeekends();
    if (cached.length > 0) {
      setWeekends(cached.filter((w) => w.status === "active"));
    } else {
      loadWeekends().then(() => {
        setWeekends(getWeekends().filter((w) => w.status === "active"));
      });
    }
  }, []);

  const buttonFontSize = isMobile
    ? isSmall
      ? "min(2rem, 7vw)"
      : isMedium
        ? "2rem"
        : "2.5rem"
    : "1.25rem";
  const buttonHeight = isMobile ? (isSmall ? "2rem" : isMedium ? "3rem" : "4rem") : "2rem";

  const sharedButtonProps = {
    display: "inline-flex" as const,
    fontSize: buttonFontSize,
    height: buttonHeight,
    fontWeight: "normal",
    padding: "0rem",
    fontFamily: "UAFSans-Book",
  };

  const navButtonProps = {
    ...sharedButtonProps,
    textColor: "var(--white)",
    hoverTextColor: "var(--black)",
    paddingRight: "1.5rem",
  };

  const popupButtonProps = {
    ...sharedButtonProps,
    textColor: "var(--black)",
    hoverTextColor: "var(--white)",
  };

  const dividerStyles = isMobile ? styles.dividerMobile : styles.dividerDesktop;
  const blackDivider = { ...dividerStyles, backgroundColor: "var(--black)" };

  const navigateTo = (path: string) => {
    onClose();
    navigate(path);
  };

  const scrollOrNavigate = (key: string) => {
    onClose();
    if (location.pathname === "/") {
      scrollToRef(getScrollRefByKey(key));
    } else {
      navigate("/", { state: { scrollTo: key } });
    }
  };

  const menuWrapperStyle = isMobile ? styles.menuWrapperMobile : styles.menuWrapperDesktop;

  const menuContainerStyle = isMobile
    ? styles.menuContainerMobile
    : { ...styles.menuContainerDesktop, paddingLeft: isDesktopSmall ? "1.5rem" : "4vw" };

  return (
    <div style={menuWrapperStyle}>
      {!isMobile && (
        <div style={styles.leftSide}>
          <img
            src="/assets/icons/210-logo-big-bw.svg"
            alt="логотип"
            style={styles.icon}
            onClick={onClose}
          />
          <div style={styles.leftButtonRow} onClick={onClose}>
            <Button
              text="ЗАКРИТИ"
              textColor="var(--white)"
              hoverTextColor="var(--orange)"
              fontFamily="UAFSans-Book"
              padding="0rem"
            />
            <img
              src="/assets/icons/close.svg"
              style={{ width: "2rem", height: "2rem" }}
              alt="закрити"
            />
          </div>
        </div>
      )}

      <div style={menuContainerStyle}>
        <div
          style={{ ...styles.navigationColumn, alignItems: isMobile ? "flex-start" : "flex-end" }}
        >
          {/* Тестовий вишкіл — dynamic weekends */}
          {weekends.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                width: "100%",
              }}
              onMouseEnter={() => setIsHoveringTest(true)}
              onMouseLeave={() => setIsHoveringTest(false)}
            >
              {/* Desktop popup */}
              {!isMobile && (
                <div style={{ position: "absolute" }}>
                  <div
                    style={{
                      ...styles.popupContainer,
                      display: isHoveringTest ? "flex" : "none",
                    }}
                  >
                    {weekends.map((weekend, idx) => (
                      <React.Fragment key={weekend.id}>
                        <Button
                          text={weekend.name.toUpperCase()}
                          {...popupButtonProps}
                          textAlign="start"
                          onClick={() => navigateTo(`/${weekend.slug}`)}
                        />
                        {idx < weekends.length - 1 && (
                          <div style={{ ...blackDivider, marginRight: "0rem" }} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}

              {/* Dropdown trigger */}
              <div
                style={{
                  ...styles.menuButton,
                  width: "100%",
                  fontSize: buttonFontSize,
                  height: buttonHeight,
                  justifyContent: isMobile ? "space-between" : "end",
                  marginRight: isMobile ? "" : "1.5rem",
                }}
                onClick={() => setIsOpenTest((prev) => !prev)}
              >
                <p>ТЕСТОВИЙ ВИШКІЛ</p>
                <img
                  src="/assets/icons/arrow-pag.svg"
                  style={{
                    width: isMobile ? "1.7rem" : "1.2rem",
                    transform: isMobile
                      ? isOpenTest
                        ? "rotate(90deg)"
                        : "rotate(-90deg)"
                      : "rotate(180deg)",
                  }}
                />
              </div>

              {/* Mobile expanded list */}
              {isMobile && isOpenTest && (
                <>
                  <div style={blackDivider} />
                  <div style={{ paddingLeft: "1.5rem", width: "100%" }}>
                    {weekends.map((weekend, idx) => (
                      <React.Fragment key={weekend.id}>
                        <Button
                          text={weekend.name.toUpperCase()}
                          {...popupButtonProps}
                          textAlign="start"
                          onClick={() => navigateTo(`/${weekend.slug}`)}
                        />
                        {idx < weekends.length - 1 && <div style={blackDivider} />}
                      </React.Fragment>
                    ))}
                  </div>
                </>
              )}

              <div style={blackDivider} />
            </div>
          )}

          <Button
            text="ПАТРОНАТНА СЛУЖБА"
            {...navButtonProps}
            onClick={() => navigateTo("/patronat")}
          />
          <div style={dividerStyles} />
          <Button
            text="КОНТАКТИ"
            {...navButtonProps}
            onClick={() => scrollOrNavigate("contacts")}
          />
          <div style={dividerStyles} />
          <Button
            text="МЕРЧ"
            {...navButtonProps}
            textAlign="start"
            onClick={() => navigateTo("/shop")}
          />
          <div style={dividerStyles} />
          <Button
            text="ПРО ПОЛК"
            {...navButtonProps}
            textAlign="start"
            onClick={() => navigateTo("/pro_polk")}
          />
          <div style={dividerStyles} />
          <Button text="ВІДЕО ПРО НАС" {...navButtonProps} onClick={() => scrollOrNavigate("media")} />
          <div style={dividerStyles} />

          {/* Підтримати */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              width: "100%",
            }}
            onMouseEnter={() => setIsHoveringSupport(true)}
            onMouseLeave={() => setIsHoveringSupport(false)}
          >
            {!isMobile && (
              <div style={{ position: "absolute" }}>
                <div
                  style={{ ...styles.popupContainer, display: isHoveringSupport ? "flex" : "none" }}
                >
                  <Button
                    text="ТЕРМІНОВИЙ ЗБІР"
                    {...popupButtonProps}
                    minWidth="220px"
                    textAlign="end"
                    textColor="var(--white)"
                    hoverTextColor="var(--black)"
                    external_link={getMainInfoByName("supportLink")}
                  />
                  <div style={{ ...dividerStyles, marginRight: "0rem" }} />
                  <Button
                    text="ПІДПИСКА"
                    {...popupButtonProps}
                    textColor="var(--white)"
                    hoverTextColor="var(--black)"
                    external_link={getMainInfoByName("subscriptionLink")}
                  />
                </div>
              </div>
            )}
            <div
              style={{
                ...styles.menuButton,
                color: "var(--white)",
                width: "100%",
                fontSize: buttonFontSize,
                height: buttonHeight,
                justifyContent: isMobile ? "space-between" : "end",
                marginRight: isMobile ? "" : "1.5rem",
              }}
              onClick={() => setIsOpenSupport((prev) => !prev)}
            >
              <p>ПІДТРИМАТИ</p>
              <img
                src="/assets/icons/arrow-pag-white.svg"
                style={{
                  width: isMobile ? "1.7rem" : "1.2rem",
                  transform: isMobile
                    ? isOpenSupport
                      ? "rotate(90deg)"
                      : "rotate(-90deg)"
                    : "rotate(180deg)",
                }}
              />
            </div>
            {isMobile && isOpenSupport && (
              <>
                <div style={dividerStyles} />
                <div style={{ paddingLeft: "1.5rem", width: "100%" }}>
                  <Button
                    text="ТЕРМІНОВИЙ ЗБІР"
                    {...navButtonProps}
                    textAlign="start"
                    external_link={getMainInfoByName("supportLink")}
                  />
                  <div style={dividerStyles} />
                  <Button
                    text="ПІДПИСКА"
                    {...navButtonProps}
                    textAlign="start"
                    external_link={getMainInfoByName("subscriptionLink")}
                  />
                </div>
              </>
            )}
          </div>

          <div style={blackDivider} />
          <Button
            text="ПРИЄДНАТИСЯ"
            {...sharedButtonProps}
            textColor="var(--black)"
            hoverTextColor="var(--white)"
            paddingRight="1.5rem"
            external_link={getMainInfoByName("anketaLink")}
          />
        </div>

        <div
          style={{
            ...dividerStyles,
            backgroundColor: "var(--black)",
            position: "relative",
            right: isMobile ? "" : "1.5rem",
          }}
        />

        <SocialMediaRow
          useMenu={true}
          rowStyle={{
            justifyContent: isMobile ? "start" : "end",
            gap: isMobile ? "2rem" : "1rem",
            position: "relative",
            left: isMobile ? "-25px" : "0px",
            paddingRight: "1.5rem",
          }}
          size={isMobile ? "min(5rem, 15vw)" : "3rem"}
        />
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  menuWrapperMobile: {
    position: "fixed",
    top: "var(--header-height)",
    left: 0,
    width: "100vw",
    height: "calc(100vh - var(--header-height))",
    zIndex: 1000,
    backgroundColor: "var(--olive)",
    overflowY: "scroll",
    overflowX: "hidden",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  menuContainerMobile: {
    display: "flex",
    flexDirection: "column",
    justifySelf: "center",
    alignSelf: "center",
    width: "var(--main-container-width)",
    padding: "3rem 0rem",
    margin: "0rem 1rem",
  },
  menuWrapperDesktop: {
    position: "fixed",
    height: "100vh",
    top: 0,
    width: "calc(var(--main-container-margin) + 18rem)",
    zIndex: 10000,
    backgroundColor: "var(--olive)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
    overflowY: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    overflow: "visible",
  },
  menuContainerDesktop: {
    display: "flex",
    flexDirection: "column",
    paddingTop: "1.5rem",
    paddingBottom: "1.5rem",
    paddingLeft: "5vw",
    width: "100%",
  },
  leftSide: {
    height: "var(--header-height)",
    backgroundColor: "var(--black)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    paddingLeft: "calc(calc(99vw - var(--main-container-width)) / 2)",
  },
  navigationColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
  },
  dividerMobile: {
    width: "100%",
    backgroundColor: "var(--white)",
    margin: "2rem 0rem",
    height: "1.5px",
    padding: "0rem 1.5rem",
  },
  dividerDesktop: {
    width: "100%",
    backgroundColor: "var(--white)",
    height: "1.5px",
    margin: "1rem 0rem",
    marginRight: "1.5rem",
    padding: "0rem 1.5rem",
  },
  leftButtonRow: {
    display: "flex",
    flexDirection: "row",
    gap: "1.5rem",
    position: "relative",
    left: "-30px",
    cursor: "pointer",
    padding: "1rem 0rem",
  },
  icon: {
    height: "var(--header-height)",
    position: "relative",
    left: "-46.5px",
    cursor: "pointer",
    filter: "invert(1)",
  },
  menuButton: {
    color: "var(--black)",
    display: "flex",
    flexDirection: "row",
    gap: "0.5rem",
    alignItems: "center",
    fontWeight: "normal",
    padding: "0rem",
    fontFamily: "UAFSans-Book",
    userSelect: "none",
    cursor: "pointer",
  },
  popupContainer: {
    display: "flex",
    minWidth: "220px",
    flexDirection: "column",
    backgroundColor: "var(--olive)",
    bottom: "1.5rem",
    left: "calc(100%)",
    padding: "1.5rem 2rem",
    position: "relative",
    alignItems: "end",
    zIndex: "1",
    boxShadow: "10px 8px 24px rgba(0, 0, 0, 0.2)",
  },
};

export default Menu;